// # Tutor Overview Component
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { 
  FaChalkboardTeacher, 
  FaUsers, 
  FaMoneyBillWave, 
  FaCalendarAlt 
} from 'react-icons/fa';
import './tutor_overview.css';

// # Helper function to get member name from ID
const getMemberName = (memberId) => {
  const memberNames = {
    'ahmed_hadidy': 'Ahmed Elhadidy',
    'nabil_eid': 'Nabil Eid',
    'ahmed_elshafie': 'Ahmed Elshafie',
    'ahmed_elshamy': 'Ahmed Elshamy',
    'ahmed_elsherbiny': 'Ahmed Elsherbiny',
    'budget': 'Budget Account'
  };
  
  return memberNames[memberId] || memberId;
};

/**
 * Tutor Overview Component
 * Displays performance metrics for all tutors
 */
const TutorOverview = ({ dateRange, tutorList }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tutorData, setTutorData] = useState({
    tutorPerformance: [],
    tutorRevenueOverTime: {},
    tutorStudentCounts: [],
    tutorProductDistribution: {},
    totalTutorRevenue: 0,
    averageTutorRevenue: 0,
    topPerformingTutor: '',
    tutorGrowthRates: {}
  });

  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // # Fetch and process tutor data
  useEffect(() => {
    const fetchTutorData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // # Get all documents from the financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Extract all payment records related to tutors
        let tutorPayments = [];
        
        // # Process each document
        financialSnap.docs.forEach(doc => {
          // # Document ID now contains year_month format (e.g., "2024_03")
          const docId = doc.id;
          const yearMonthData = doc.data();
          
          // # Check if payments map exists in the document
          if (yearMonthData.payments) {
            // # Extract payments from the payments map
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions with tutor hosting products
              if (paymentId.startsWith('payment_') && 
                  paymentData.transaction_type === 'payment' && 
                  paymentData.pay?.product && 
                  paymentData.pay.product.includes('tutor_hosting')) {
                
                // # Extract payment date from metadata
                const paymentDate = new Date(paymentData.metadata?.created_at || '');
                
                // # Apply date range filter if provided
                if (dateRange) {
                  const startDate = new Date(dateRange.startDate);
                  const endDate = new Date(dateRange.endDate);
                  
                  // # Set hours for proper date comparison
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(23, 59, 59, 999);
                  
                  // # Skip if outside date range
                  if (paymentDate < startDate || paymentDate > endDate) {
                    return;
                  }
                }
                
                // # Add payment to collection with all necessary fields - directly use stored values
                tutorPayments.push({
                  id: paymentId,
                  doc_id: docId,
                  timestamp: paymentDate,
                  egp_total: parseFloat(paymentData.pay?.egp_total) || 0,
                  egp_net: parseFloat(paymentData.pay?.egp_net) || 0,
                  product: paymentData.pay?.product || 'unknown',
                  nationality: paymentData.user?.nationality || 'Unknown',
                  email: paymentData.user?.email || '',
                  name: paymentData.user?.name || '',
                  userId: paymentData.user?.userId || '',
                  tutor_id: paymentData.tutor?.uid || '',
                  tutor_name: paymentData.tutor?.tutor_name || '',
                  // # Directly use the shares data without recalculation
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        });
        
        // # Process tutor data
        processTutorData(tutorPayments, tutorList);
      } catch (error) {
        console.error('Error fetching tutor data:', error);
        setError('Failed to load tutor data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTutorData();
  }, [dateRange, tutorList]);
  
  // # Process tutor data - simplified to use pre-calculated values
  const processTutorData = (payments, tutorList) => {
    try {
      // # Initialize tutor performance data
      const tutorPerformance = [];
      const tutorRevenueOverTime = {};
      const tutorStudentCounts = {};
      const tutorProductDistribution = {};
      let totalTutorRevenue = 0;
      
      // # Create a map of tutors for easy lookup
      const tutorMap = {};
      tutorList.forEach(tutor => {
        tutorMap[tutor.id] = {
          id: tutor.id,
          name: tutor.name || getMemberName(tutor.id),
          totalRevenue: 0,
          studentCount: new Set(),
          paymentCount: 0
        };
      });
      
      // # Process each payment
      payments.forEach(payment => {
        // # Get tutor ID
        const tutorId = payment.tutor_id;
        
        // # Skip if tutor not found in the list
        if (!tutorId || !tutorMap[tutorId]) {
          return;
        }
        
        // # Update tutor data
        const tutor = tutorMap[tutorId];
        
        // # Use the pre-calculated share amount for this tutor
        const tutorShare = payment.shares[tutorId]?.net_share_egp || 0;
        tutor.totalRevenue += tutorShare;
        totalTutorRevenue += payment.egp_total;
        
        // # Track unique students
        tutor.studentCount.add(payment.email);
        
        // # Count payments
        tutor.paymentCount += 1;
        
        // # Track revenue over time
        const date = new Date(payment.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!tutorRevenueOverTime[tutorId]) {
          tutorRevenueOverTime[tutorId] = {};
        }
        
        tutorRevenueOverTime[tutorId][monthKey] = (tutorRevenueOverTime[tutorId][monthKey] || 0) + tutorShare;
        
        // # Track product distribution
        if (!tutorProductDistribution[tutorId]) {
          tutorProductDistribution[tutorId] = {};
        }
        
        tutorProductDistribution[tutorId][payment.product] = (tutorProductDistribution[tutorId][payment.product] || 0) + 1;
      });
      
      // # Calculate tutor growth rates
      const tutorGrowthRates = {};
      
      Object.entries(tutorRevenueOverTime).forEach(([tutorId, monthlyRevenue]) => {
        const months = Object.keys(monthlyRevenue).sort();
        
        if (months.length >= 2) {
          const firstMonth = months[0];
          const lastMonth = months[months.length - 1];
          
          const firstRevenue = monthlyRevenue[firstMonth];
          const lastRevenue = monthlyRevenue[lastMonth];
          
          if (firstRevenue > 0) {
            tutorGrowthRates[tutorId] = ((lastRevenue - firstRevenue) / firstRevenue) * 100;
          } else {
            tutorGrowthRates[tutorId] = 100; // Default to 100% growth if starting from zero
          }
        } else {
          tutorGrowthRates[tutorId] = 0; // No growth data available
        }
      });
      
      // # Convert tutor data to array format for display
      Object.values(tutorMap).forEach(tutor => {
        if (tutor.paymentCount > 0) {
          tutorPerformance.push({
            ...tutor,
            studentCount: tutor.studentCount.size
          });
        }
      });
      
      // # Sort by total revenue
      tutorPerformance.sort((a, b) => b.totalRevenue - a.totalRevenue);
      
      // # Set the processed data
      setTutorData({
        tutorPerformance,
        tutorRevenueOverTime,
        tutorProductDistribution,
        tutorGrowthRates,
        totalTutorRevenue
      });
    } catch (error) {
      console.error('Error processing tutor data:', error);
      setError('Failed to process tutor data.');
    }
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  // # Format month for display
  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  
  // # Render loading state
  if (loading) {
    return <div className="fnc_ttr_ovr_loading">Loading tutor data...</div>;
  }
  
  // # Render error state
  if (error) {
    return <div className="fnc_ttr_ovr_error">{error}</div>;
  }
  
  return (
    <div className="fnc_ttr_ovr_container">
      <div className="fnc_ttr_ovr_header">
        <h2><FaChalkboardTeacher /> Tutor Performance Overview</h2>
        <p>Analysis of tutor performance metrics across all products</p>
      </div>
      
      <div className="fnc_ttr_ovr_summary">
        <div className="fnc_ttr_ovr_summary_card">
          <div className="fnc_ttr_ovr_summary_icon">
            <FaChalkboardTeacher />
          </div>
          <div className="fnc_ttr_ovr_summary_content">
            <h3>Total Tutors</h3>
            <p>{tutorData.tutorPerformance.length}</p>
          </div>
        </div>
        
        <div className="fnc_ttr_ovr_summary_card">
          <div className="fnc_ttr_ovr_summary_icon">
            <FaMoneyBillWave />
          </div>
          <div className="fnc_ttr_ovr_summary_content">
            <h3>Total Revenue</h3>
            <p>{formatCurrency(tutorData.totalTutorRevenue)}</p>
          </div>
        </div>
        
        <div className="fnc_ttr_ovr_summary_card">
          <div className="fnc_ttr_ovr_summary_icon">
            <FaUsers />
          </div>
          <div className="fnc_ttr_ovr_summary_content">
            <h3>Total Students</h3>
            <p>{tutorData.tutorPerformance.reduce((sum, tutor) => sum + tutor.studentCount, 0)}</p>
          </div>
        </div>
        
        <div className="fnc_ttr_ovr_summary_card">
          <div className="fnc_ttr_ovr_summary_icon">
            <FaCalendarAlt />
          </div>
          <div className="fnc_ttr_ovr_summary_content">
            <h3>Top Tutor</h3>
            <p>{tutorData.tutorPerformance[0]?.name || 'N/A'}</p>
          </div>
        </div>
      </div>
      
      {tutorData.tutorPerformance.length > 0 ? (
        <>
          {/* Revenue Comparison Chart */}
          <div className="fnc_ttr_ovr_chart">
            <h3>Tutor Revenue Comparison</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={tutorData.tutorPerformance.map(tutor => ({
                  name: tutor.name,
                  revenue: tutor.totalRevenue
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Student Count Chart */}
          <div className="fnc_ttr_ovr_chart">
            <h3>Student Count by Tutor</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={tutorData.tutorStudentCounts}
                margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Students" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Performance Details Table */}
          <div className="fnc_ttr_ovr_details">
            <h3>Tutor Performance Details</h3>
            <table className="fnc_ttr_ovr_table">
              <thead>
                <tr>
                  <th>Tutor</th>
                  <th>Total Revenue</th>
                  <th>Students</th>
                  <th>Payments</th>
                  <th>Avg. Revenue/Student</th>
                  <th>Growth Rate</th>
                </tr>
              </thead>
              <tbody>
                {tutorData.tutorPerformance.map(tutor => (
                  <tr key={tutor.id}>
                    <td>{tutor.name}</td>
                    <td>{formatCurrency(tutor.totalRevenue)}</td>
                    <td>{tutor.studentCount}</td>
                    <td>{tutor.paymentCount}</td>
                    <td>{formatCurrency(tutor.studentCount > 0 ? tutor.totalRevenue / tutor.studentCount : 0)}</td>
                    <td>{tutorData.tutorGrowthRates[tutor.id]?.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="fnc_ttr_ovr_no_data">
          <p>No tutor data available for the selected date range.</p>
        </div>
      )}
    </div>
  );
};

export default TutorOverview;
